// Bootstraps UXtweak (Session Recording spike, MANCRE-5570).
// DEV ONLY.
//
// VUE_APP_UXTWEAK_ENABLED is deliberately the ONLY gate. Every deploy job builds with
// `npm run build`, so NODE_ENV === 'production' on the DEV deployment as well: a NODE_ENV
// guard would switch the recorder off on exactly the environment this spike targets.
//
// VENDOR CODE. The body of runUxtweakBootstrap() below is the UXtweak bootstrap snippet
// transcribed from the real snippet (UXtweak dashboard -> avatar menu -> "My snippet"),
// retrieved 2025-10-15. It is NOT a hand-written loader: the snippet does far more than
// append one script tag (sessionStorage cache, POST to the snippet API, one inline stub per
// active study), so it goes in verbatim inside our guard. The minified variables were renamed
// and the code reformatted for readability and eslint only — the behaviour is unchanged: same
// sessionStorage cache key, same POST target, same document.URL request body, same per-study
// inline <script> source text, same insertion into <head>. The only substitution is the
// snippet key, which is read from VUE_APP_UXTWEAK_KEY instead of being hardcoded. Do not
// "improve" this function; re-transcribe it if the vendor reissues it.
const UXTWEAK_KEY = process.env.VUE_APP_UXTWEAK_KEY

// Literal vendor values, straight out of the snippet.
const BOOTSTRAP_API = 'https://api.uxtweak.com/snippet/'
const CACHE_PREFIX = 'uxt:'

// Source of the per-study inline stub, up to its last three arguments (collector URL, config,
// global queue name), which are server-supplied and appended per study below. The stub names the
// global queue function itself, so we must not pre-create it — see the privacy note in
// initUxtweak().
const STUB_SOURCE_HEAD = [
  '(function(u,x,t,w,e,a,k){',
  'a=u[e]=function(){a.q.push(arguments)};',
  'a.q=[];',
  'a.t=+new Date;',
  'a.c=w;',
  "k=x.createElement('script');",
  'k.async=1;',
  'k.src=t;',
  "x.getElementsByTagName('head')[0].appendChild(k)",
  '})(window,document,'
].join('')

// Hard off switch: flag not explicitly 'true', missing snippet key, or an E2E build => no-op.
const isUxtweakEnabled = () => process.env.VUE_APP_UXTWEAK_ENABLED === 'true'
  && !!UXTWEAK_KEY
  && process.env.VUE_APP_E2E_ENABLED !== 'true'

// Idempotency: a double call must not bootstrap twice.
let isBootstrapRequested = false

const runUxtweakBootstrap = key => {
  const cacheKey = CACHE_PREFIX + key

  // Vendor: a(v) — cache the raw response, then inject one inline stub per active study.
  const processResponse = response => {
    try {
      sessionStorage.setItem(cacheKey, response)
    }
    catch (error) {
      // Vendor swallows this: private browsing / storage disabled must not break the page.
    }

    // The response is a JSON array of the studies currently active for this snippet key.
    const studies = JSON.parse(response)

    studies.forEach(study => {
      const stub = document.createElement('script')

      // study.u = collector script URL, study.c = study config, study.g = global queue name.
      // All three are server-supplied; there is no static collector URL to configure.
      stub.text = `${ STUB_SOURCE_HEAD }'${ study.u }',${ JSON.stringify(study.c) },'${ study.g }')`
      document.getElementsByTagName('head')[0].appendChild(stub)
    })
  }

  let cached

  try {
    cached = sessionStorage.getItem(cacheKey)
  }
  catch (error) {
    // Same as above — fall through to the network request.
  }

  // Cache hit: replay the cached study list, no network call for the rest of this tab's session.
  if (cached) {
    processResponse(cached)

    return
  }

  const request = new XMLHttpRequest()

  request.onreadystatechange = () => {
    if (request.readyState === 4 && request.status === 200) processResponse(request.responseText)
  }
  request.open('POST', BOOTSTRAP_API + key)
  request.send(document.URL)
}

export const initUxtweak = () => {
  if (isBootstrapRequested || !isUxtweakEnabled()) return

  isBootstrapRequested = true

  // PRIVACY: there are deliberately no client-side consent calls here.
  // 1. The dashboard privacy settings (identify users via API, store IP address, record form
  //    inputs — all switched off) are now the authoritative and ONLY privacy control.
  // 2. The consent API cannot be called from here yet: the real snippet has the collector stub
  //    assign the queue global under a name the SERVER chooses (study.g above) and overwrite
  //    anything already sitting under it, so the name has to be OBSERVED at runtime. Guessing
  //    it would silently drop the calls — which is how an earlier hand-written window.uxtr
  //    queue in this file was wrong.
  // 3. Re-adding client-side consent calls is a follow-up once that name is known.
  //    Commands and the (command, decision, consentText) signature are documented at
  //    https://www.uxtweak.com/help/privacy-and-security-verify-user-consent-api/

  // FAILURE ISOLATION — ours, not the vendor's. The vendor ships the bootstrap as its own
  // <script> tag, where a throw kills that tag and nothing else. Here it runs inside src/main.js
  // module evaluation, above createApp(App), and the cache-hit path parses synchronously: a
  // cached non-JSON 200 (proxy/WAF interstitial, vendor format change) would abort app boot and
  // leave a white screen. Catch it here so the worst case is "no recording" instead of "no app".
  try {
    runUxtweakBootstrap(UXTWEAK_KEY)
  }
  catch (error) {
    // The bad response was cached before it was parsed, so drop it: otherwise the tab replays it
    // on every load for the rest of its session. Next load retries the POST instead.
    try {
      sessionStorage.removeItem(CACHE_PREFIX + UXTWEAK_KEY)
    }
    catch (storageError) {
      // Storage unavailable — nothing was cached, nothing to clear.
    }
  }
}

// Router hook. UXtweak does not publicly document an SPA virtual-pageview API, so this relies
// on the collector's own History API detection and only records a local breadcrumb list, kept
// so recorded navigations can be compared against what shows up in the dashboard.
const BREADCRUMB_LIMIT = 500

export const trackUxtweakPageView = (to = {}) => {
  // Gated on the bootstrap actually having been requested, not just on the flag: otherwise
  // the breadcrumb list fills up for a session that is not being recorded, which would make it
  // look like the dashboard lost pageviews when nothing was ever recording. Caveat: this flag
  // only means the bootstrap POST was ISSUED — if it failed or returned non-200, the list still
  // fills, so check the bootstrap actually succeeded (Network tab) before trusting this list.
  if (!isBootstrapRequested) return

  // eslint-disable-next-line no-underscore-dangle
  window.__uxtweakRoutes = window.__uxtweakRoutes || []
  // eslint-disable-next-line no-underscore-dangle
  window.__uxtweakRoutes.push({ name: to.name, path: to.fullPath, at: Date.now() })
  // Debug aid only — keep it bounded for long-lived tabs.
  // eslint-disable-next-line no-underscore-dangle
  if (window.__uxtweakRoutes.length > BREADCRUMB_LIMIT) window.__uxtweakRoutes.shift()

  // If it turns out route changes are NOT auto-detected by the collector, add the
  // vendor-confirmed call here. Do not guess a function name — confirm with UXtweak support
  // first, since the queue global's name is server-assigned (see initUxtweak above).
}
